import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useMemo } from 'react';
import { IProductTexture } from '../../../../entities-generated/common/product-texture';
import { IRatingBarProps, RatingBar } from '../../../components/rating-bar';
import { BwjCard } from '../../../../framework/components/bwj-layout';
import { ProductVariantSelector } from '../pdct-variant-select';
import { BwjButton, BwjButtonType } from '../../../../framework/components/bwj-button';
import { ProductImageBrowser } from '../pdct-image-browser';
import { DoorRecommendation } from '../../../../entities-generated/common/door-recommendation';

interface IProductTileProps {
  id: string;
  idPrefix: string;
  imageUrls: string[];
  brand: string;
  description: string;
  textures: IProductTexture[];
  price: number;
  ratingProps?: IRatingBarProps | undefined;
  recommendation?: DoorRecommendation | undefined;
  onProductSelected: () => void;
}

const getStyleClasses = (theme: ITheme) => {
  const root: IStyle = {
    boxSizing: 'border-box',
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    width: 340,
    padding: 0,
    margin: 0,
    position: 'relative',

    '& *': {
      boxSizing: 'border-box',
    },
  };

  const infoContainer: IStyle = {
    padding: '0.75rem',
  };

  const textureContainer: IStyle = {
    paddingBottom: '0.5rem',
  };

  const brandContainer: IStyle = {
    height: theme.fonts.xSmall.lineHeight,
    overflow: 'hidden',
  };

  const brand: IStyle = {
    fontSize: theme.fonts.xSmall.fontSize,
    lineHeight: theme.fonts.xSmall.lineHeight,
    fontWeight: 400,
    margin: 0,
    padding: 0,
  };

  const descriptionContainer: IStyle = {
    height: '4.3rem',
    overflow: 'hidden',
  };

  const description: IStyle = {
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    fontWeight: 600,
    margin: 0,
    padding: 0,
  };

  const ratingContainer: IStyle = {
    paddingTop: '0.75rem',
    paddingBottom: '1rem',
    height: `calc(${theme.fonts.medium.lineHeight} + 1.75rem)`,
  };

  const priceContainer: IStyle = {
    padding: 0,
    fontSize: theme.fonts.xxLarge.fontSize,
    height: theme.fonts.xxLarge.lineHeight,
  };

  const priceSup: IStyle = {
    fontSize: theme.fonts.small.fontSize,
    lineHeight: theme.fonts.small.lineHeight,
    fontWeight: 600,
    verticalAlign: 'super',
  };

  const price: IStyle = {
    fontSize: theme.fonts.xxLarge.fontSize,
    lineHeight: theme.fonts.xxLarge.lineHeight,
    fontWeight: 600,
  };

  const actionContainer: IStyle = {
    paddingTop: '1rem',

    '& button': {
      width: '100%',
    },
  };

  return mergeStyleSets({
    root,
    infoContainer,
    textureContainer,
    brandContainer,
    brand,
    descriptionContainer,
    description,
    ratingContainer,
    priceContainer,
    priceSup,
    price,
    actionContainer,
  });
}

export const ProductTile: React.FunctionComponent<IProductTileProps> = (props) => {
  const { imageUrls, brand, description } = props;
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const literalPrice = props.price / 100;
  const dollars = Math.floor(literalPrice);
  const cents = ((literalPrice - dollars) * 100).toString().padStart(2, "0");
  const images = useMemo(() => {
    return imageUrls.map(src => {
      return {
        src,
        alt: `${brand} ${description}`,
      };
    });
  }, [imageUrls, brand, description]);

  return (
    <BwjCard
      className={cs.root}
    >
      <ProductImageBrowser
        id={props.id}
        idPrefix={props.idPrefix}
        images={images}
        imageWidth={340}
        recommendation={props.recommendation}
      />
      <div className={cs.infoContainer}>
        <div className={cs.textureContainer}>
          <ProductVariantSelector
            swatchSize={1.83}
            options={props.textures}
            maxOptionDisplay={7}
            showLabels={false}
          />
        </div>
        <div className={cs.brandContainer}>
          <h4 className={cs.brand}>{props.brand}</h4>
        </div>
        <div className={cs.descriptionContainer}>
          <h3 className={cs.description}>{props.description}</h3>
        </div>
        <div className={cs.ratingContainer}>
          {!!props.ratingProps &&
          <RatingBar
            rating={props.ratingProps.rating}
            reviewerCount={props.ratingProps.reviewerCount}
            rootStyle={{ width: '67%' }}
          />}
        </div>
        {literalPrice > 0 &&
        <div
          className={cs.priceContainer}
          aria-label={`Price $${literalPrice.toFixed(2)}`}
        >
          <sup className={cs.priceSup}>$</sup>
          <span className={cs.price}>{dollars.toLocaleString()}</span>
          <sup className={cs.priceSup}>{`.${cents}`}</sup>
        </div>}
        <div className={cs.actionContainer}>
          <BwjButton
              buttonStyle={BwjButtonType.PrimaryStandard}
              text={'View details'}
              onClick={props.onProductSelected}
            />
        </div>
      </div>
    </BwjCard>

  );
};
